import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import type {
  FiscalDocumentStatus,
  FiscalDocumentSummary,
  FiscalRecipient,
} from '@aeris/contracts'
import { MONEY_SCALE, formatMoney, fromString } from '@aeris/money'
import {
  Badge,
  Button,
  Combobox,
  Dialog,
  DialogContent,
  DialogFooter,
  EmptyState,
  Field,
  InlineAlert,
  Input,
  ListToolbar,
  MoneyInput,
  Page,
  PageHeader,
  Pagination,
  QueryState,
  SearchInput,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
  Textarea,
} from '@aeris/ui'

import {
  ApiError,
  cancelFiscalDocument,
  createFiscalCorrection,
  createNfse,
  fetchFiscalCorrections,
  fetchFiscalDocuments,
  fetchFiscalServiceItems,
  fetchParties,
  fetchParty,
  fiscalDocumentFileUrl,
  retryFiscalDocument,
} from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'
import { lookupCep } from '../lib/cep.ts'

/**
 * As notas da empresa (F372, ADR-0051).
 *
 * "Notas fiscais" e não "notas": no Aeris, "nota" sozinha é o recibo. A situação
 * é honesta: `incerta` diz que o governo não respondeu, porque dizer "erro" faria
 * alguém emitir de novo uma nota que pode já ter saído.
 */

const QUERY_KEY = ['fiscal-documents']
const PAGE_SIZE = 50

const STATUS_LABEL: Readonly<Record<FiscalDocumentStatus, string>> = {
  pendente: 'Na fila',
  processando: 'Enviando',
  autorizada: 'Autorizada',
  rejeitada: 'Recusada',
  incerta: 'Sem resposta, conferindo',
  cancelando: 'Cancelando',
  cancelada: 'Cancelada',
}

const STATUS_TONE: Readonly<
  Record<FiscalDocumentStatus, 'success' | 'warning' | 'danger' | 'neutral'>
> = {
  pendente: 'neutral',
  processando: 'neutral',
  autorizada: 'success',
  rejeitada: 'danger',
  incerta: 'warning',
  cancelando: 'warning',
  cancelada: 'neutral',
}

const KIND_LABEL = {
  nfse: 'Serviço',
  nfe: 'Produto',
  nfce: 'Consumidor',
} as const

const message = (error: unknown, fallback: string): string =>
  error instanceof ApiError ? error.message : fallback

const money = (wire: string): string =>
  formatMoney(fromString(wire, MONEY_SCALE))

const dateOf = (day: string): string => day.split('-').reverse().join('/')

/** Enquanto houver nota andando, a lista se atualiza sozinha. */
const OPEN: readonly FiscalDocumentStatus[] = [
  'pendente',
  'processando',
  'incerta',
  'cancelando',
]

export const NotasFiscaisPage = (): ReactNode => {
  const { allows } = useAuth()
  const [term, setTerm] = useState('')
  const [status, setStatus] = useState('')
  const [page, setPage] = useState(1)
  const [creating, setCreating] = useState(false)
  const [cancelling, setCancelling] = useState<FiscalDocumentSummary | null>(
    null
  )
  const [correcting, setCorrecting] = useState<FiscalDocumentSummary | null>(
    null
  )
  const queryClient = useQueryClient()

  const list = useQuery({
    queryKey: [...QUERY_KEY, status, term, page],
    queryFn: ({ signal }) =>
      fetchFiscalDocuments(
        {
          status: status === '' ? undefined : status,
          q: term,
          page,
          pageSize: PAGE_SIZE,
        },
        signal
      ),
    refetchInterval: (query) =>
      query.state.data?.documents.some((item) => OPEN.includes(item.status)) ===
      true
        ? 5_000
        : false,
  })

  const retry = useMutation({
    mutationFn: retryFiscalDocument,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  })

  const documents = list.data?.documents ?? []

  return (
    <Page>
      <PageHeader
        title="Notas fiscais"
        description="As notas emitidas pela empresa, com a situação de cada uma."
        actions={
          allows('fiscal.emit') ? (
            <Button
              variant="primary"
              onClick={() => {
                setCreating(true)
              }}
            >
              Nova nota de serviço
            </Button>
          ) : null
        }
      />

      <ListToolbar>
        <SearchInput
          aria-label="Buscar nota pelo cliente, documento ou número"
          placeholder="Cliente, CPF/CNPJ ou número"
          value={term}
          onChange={(event) => {
            setTerm(event.target.value)
            setPage(1)
          }}
        />
        <Select
          aria-label="Filtrar por situação"
          value={status}
          onChange={(event) => {
            setStatus(event.target.value)
            setPage(1)
          }}
        >
          <option value="">Todas as situações</option>
          {(Object.keys(STATUS_LABEL) as FiscalDocumentStatus[]).map(
            (value) => (
              <option key={value} value={value}>
                {STATUS_LABEL[value]}
              </option>
            )
          )}
        </Select>
      </ListToolbar>

      {retry.isError ? (
        <InlineAlert tone="danger">
          {message(retry.error, 'Não foi possível enviar de novo agora.')}
        </InlineAlert>
      ) : null}

      <QueryState
        isPending={list.isPending}
        isError={list.isError}
        error={list.error}
        onRetry={() => void list.refetch()}
        what="as notas fiscais"
        isEmpty={documents.length === 0}
        empty={
          <EmptyState
            title="Nenhuma nota por aqui"
            description="A nota emitida aparece nesta lista, com o PDF e o XML."
          />
        }
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Número</TableHeaderCell>
                <TableHeaderCell>Tipo</TableHeaderCell>
                <TableHeaderCell>Cliente</TableHeaderCell>
                <TableHeaderCell>Competência</TableHeaderCell>
                <TableHeaderCell>Valor</TableHeaderCell>
                <TableHeaderCell>Situação</TableHeaderCell>
                <TableHeaderCell>Ações</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {documents.map((document) => (
                <TableRow key={document.id}>
                  <TableCell>
                    {document.number}
                    {document.environment === 'homologacao' ? (
                      <Badge tone="warning" className="ml-2">
                        Teste
                      </Badge>
                    ) : null}
                  </TableCell>
                  <TableCell>{KIND_LABEL[document.kind]}</TableCell>
                  <TableCell>{document.recipientName}</TableCell>
                  <TableCell>{dateOf(document.competence)}</TableCell>
                  <TableCell>{money(document.totalAmount)}</TableCell>
                  <TableCell>
                    <Badge tone={STATUS_TONE[document.status]}>
                      {STATUS_LABEL[document.status]}
                    </Badge>
                    {document.rejectionMessage === null ? null : (
                      <p className="mt-1 max-w-80 text-(length:--text-xs) text-(--text-secondary)">
                        {document.rejectionMessage}
                      </p>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-2">
                      {document.hasPdf ? (
                        <a
                          className="text-(length:--text-sm) underline"
                          href={fiscalDocumentFileUrl(document.id, 'pdf')}
                          target="_blank"
                          rel="noreferrer"
                        >
                          PDF
                        </a>
                      ) : null}
                      {document.hasXml ? (
                        <a
                          className="text-(length:--text-sm) underline"
                          href={fiscalDocumentFileUrl(document.id, 'xml')}
                          download={`${document.kind}-${document.number}.xml`}
                        >
                          XML
                        </a>
                      ) : null}
                      {document.canRetry && allows('fiscal.emit') ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          loading={retry.isPending}
                          onClick={() => {
                            retry.mutate(document.id)
                          }}
                        >
                          Enviar de novo
                        </Button>
                      ) : null}
                      {document.canCorrect && allows('fiscal.cancel') ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setCorrecting(document)
                          }}
                        >
                          Corrigir
                        </Button>
                      ) : null}
                      {document.canCancel && allows('fiscal.cancel') ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setCancelling(document)
                          }}
                        >
                          Cancelar
                        </Button>
                      ) : null}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Pagination
          page={page}
          pageSize={PAGE_SIZE}
          total={list.data?.total ?? 0}
          onPageChange={setPage}
        />
      </QueryState>

      {creating ? (
        <NewNfseDialog
          onClose={() => {
            setCreating(false)
          }}
        />
      ) : null}
      {correcting === null ? null : (
        <CorrectionDialog
          document={correcting}
          onClose={() => {
            setCorrecting(null)
          }}
        />
      )}
      {cancelling === null ? null : (
        <CancelDialog
          document={cancelling}
          onClose={() => {
            setCancelling(null)
          }}
        />
      )}
    </Page>
  )
}

const CorrectionDialog = ({
  document,
  onClose,
}: {
  document: FiscalDocumentSummary
  onClose: () => void
}): ReactNode => {
  const queryClient = useQueryClient()
  const queryKey = ['fiscal-corrections', document.id]
  const [text, setText] = useState('')
  const corrections = useQuery({
    queryKey,
    queryFn: ({ signal }) => fetchFiscalCorrections(document.id, signal),
  })
  const send = useMutation({
    meta: { errorShownInline: true },
    mutationFn: () =>
      createFiscalCorrection(document.id, { text: text.trim() }),
    onSuccess: async () => {
      setText('')
      await queryClient.invalidateQueries({ queryKey })
    },
  })
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <DialogContent
        title={`Carta de correção da nota ${document.number}`}
        description="Não corrige valor, imposto nem quem compra ou vende: para isso, cancele e emita outra."
      >
        <form
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault()
            send.mutate()
          }}
        >
          {(corrections.data?.corrections ?? []).map((item) => (
            <p
              key={item.id}
              className="text-(length:--text-sm) text-(--text-secondary)"
            >
              {item.sequence}ª: {item.text} (
              {item.status === 'aceita'
                ? 'aceita'
                : item.status === 'recusada'
                  ? `recusada: ${item.message ?? ''}`
                  : 'em andamento'}
              )
            </p>
          ))}
          <Field label="O que corrigir" hint="Mínimo de 15 letras.">
            {(field) => (
              <Textarea
                {...field}
                rows={3}
                value={text}
                maxLength={1000}
                onChange={(event) => {
                  setText(event.target.value)
                }}
              />
            )}
          </Field>
          {send.isError ? (
            <InlineAlert tone="danger">
              {message(send.error, 'Não foi possível pedir a correção agora.')}
            </InlineAlert>
          ) : null}
          <DialogFooter>
            <Button variant="ghost" onClick={onClose}>
              Voltar
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={send.isPending}
              disabled={text.trim().length < 15}
            >
              Enviar correção
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

const CancelDialog = ({
  document,
  onClose,
}: {
  document: FiscalDocumentSummary
  onClose: () => void
}): ReactNode => {
  const queryClient = useQueryClient()
  const [reasonCode, setReasonCode] = useState<'1' | '2' | '9'>('1')
  const [reason, setReason] = useState('')
  const cancel = useMutation({
    meta: { errorShownInline: true },
    mutationFn: (reason: string) =>
      cancelFiscalDocument(document.id, { reasonCode, reason }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY })
      onClose()
    },
  })
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <DialogContent
        title={`Cancelar a nota ${document.number}?`}
        description="O pedido vai para o governo, que pode recusar. Enquanto ele não responder, a nota continua valendo."
      >
        <form
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault()
            cancel.mutate(reason.trim())
          }}
        >
          <Field label="Motivo">
            {(field) => (
              <Select
                {...field}
                value={reasonCode}
                onChange={(event) => {
                  setReasonCode(event.target.value as '1' | '2' | '9')
                }}
              >
                <option value="1">Erro na emissão</option>
                <option value="2">Serviço não prestado</option>
                <option value="9">Outros</option>
              </Select>
            )}
          </Field>
          <Field label="Explique o motivo" hint="Mínimo de 15 letras.">
            {(field) => (
              <Textarea
                {...field}
                rows={3}
                value={reason}
                maxLength={255}
                onChange={(event) => {
                  setReason(event.target.value)
                }}
              />
            )}
          </Field>
          {cancel.isError ? (
            <InlineAlert tone="danger">
              {message(
                cancel.error,
                'Não foi possível pedir o cancelamento agora.'
              )}
            </InlineAlert>
          ) : null}
          <DialogFooter>
            <Button variant="ghost" onClick={onClose}>
              Voltar
            </Button>
            <Button
              type="submit"
              variant="danger"
              loading={cancel.isPending}
              disabled={reason.trim().length < 15}
            >
              Pedir cancelamento
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

const today = (): string =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(
    new Date()
  )

const NewNfseDialog = ({ onClose }: { onClose: () => void }): ReactNode => {
  const queryClient = useQueryClient()
  // Nasce uma vez por formulário: dois cliques não viram duas notas.
  const [clientRequestId] = useState(() => crypto.randomUUID())
  const items = useQuery({
    queryKey: ['fiscal-service-items'],
    queryFn: ({ signal }) => fetchFiscalServiceItems(signal),
  })
  const active = (items.data?.items ?? []).filter((item) => !item.archived)

  const [itemId, setItemId] = useState('')
  const [description, setDescription] = useState('')
  const [amount, setAmount] = useState('')
  const [competence, setCompetence] = useState(today)

  const [partySearch, setPartySearch] = useState('')
  const [partyId, setPartyId] = useState<string | null>(null)
  const [partyLabel, setPartyLabel] = useState<string | null>(null)
  const [documentKind, setDocumentKind] = useState<'cnpj' | 'cpf'>('cnpj')
  const [document, setDocument] = useState('')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [postalCode, setPostalCode] = useState('')
  const [street, setStreet] = useState('')
  const [number, setNumber] = useState('')
  const [district, setDistrict] = useState('')
  const [cityCode, setCityCode] = useState('')

  const parties = useQuery({
    queryKey: ['fiscal-parties', partySearch],
    queryFn: ({ signal }) =>
      fetchParties({ search: partySearch, status: 'active' }, signal),
    enabled: partySearch.trim().length >= 2,
  })

  const applyCep = async (digits: string): Promise<void> => {
    if (digits.length !== 8) return
    const found = await lookupCep(digits)
    if (found === null) return
    setCityCode(found.ibgeCode)
    if (street === '') setStreet(found.street)
    if (district === '') setDistrict(found.district)
  }

  const pickParty = async (id: string | null): Promise<void> => {
    setPartyId(id)
    if (id === null) return
    const { party } = await fetchParty(id)
    setPartyLabel(party.legalName)
    setName(party.legalName)
    setDocumentKind(party.kind === 'person' ? 'cpf' : 'cnpj')
    setDocument(party.document ?? '')
    setEmail(party.contacts.find((item) => item.kind === 'email')?.value ?? '')
    const address =
      party.addresses.find((item) => item.isMain) ?? party.addresses[0]
    if (address !== undefined) {
      setPostalCode(address.cep ?? '')
      setStreet(address.street)
      setNumber(address.number ?? '')
      setDistrict(address.district ?? '')
      setCityCode(address.ibgeCode ?? '')
      if (address.ibgeCode === null && address.cep !== null) {
        await applyCep(address.cep)
      }
    }
  }

  const addressFilled =
    postalCode !== '' &&
    street.trim() !== '' &&
    number.trim() !== '' &&
    district.trim() !== '' &&
    cityCode !== ''

  const buildRecipient = (): FiscalRecipient => ({
    documentKind,
    document,
    name: name.trim(),
    email: email.trim() === '' ? null : email.trim(),
    phone: null,
    address: addressFilled
      ? {
          cityCode,
          postalCode,
          street: street.trim(),
          number: number.trim(),
          complement: null,
          district: district.trim(),
        }
      : null,
  })

  const save = useMutation({
    meta: { errorShownInline: true },
    mutationFn: () =>
      createNfse({
        clientRequestId,
        serviceItemId: itemId,
        description: description.trim(),
        amount,
        competence,
        recipient: buildRecipient(),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY })
      onClose()
    },
  })

  const ready =
    itemId !== '' &&
    description.trim() !== '' &&
    amount !== '' &&
    document !== '' &&
    name.trim() !== '' &&
    (documentKind === 'cpf' || addressFilled)

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <DialogContent
        title="Nova nota de serviço"
        description="Depois de enviada, a nota só muda por cancelamento."
      >
        <form
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault()
            save.mutate()
          }}
        >
          {items.isSuccess && active.length === 0 ? (
            <InlineAlert tone="warning">
              Cadastre um serviço em Configurações, na aba Nota fiscal.
            </InlineAlert>
          ) : null}
          <Field label="Serviço">
            {(field) => (
              <Select
                {...field}
                value={itemId}
                onChange={(event) => {
                  const next = event.target.value
                  setItemId(next)
                  const item = active.find((entry) => entry.id === next)
                  if (item !== undefined) {
                    setDescription(item.defaultDescription)
                  }
                }}
              >
                <option value="">Escolha</option>
                {active.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Texto da nota">
            {(field) => (
              <Textarea
                {...field}
                rows={3}
                value={description}
                onChange={(event) => {
                  setDescription(event.target.value)
                }}
              />
            )}
          </Field>
          <div className="flex flex-wrap gap-3">
            <Field label="Valor" className="min-w-40 flex-1">
              {(field) => (
                <MoneyInput
                  {...field}
                  value={amount}
                  onValueChange={setAmount}
                />
              )}
            </Field>
            <Field label="Competência" className="min-w-40 flex-1">
              {(field) => (
                <Input
                  {...field}
                  type="date"
                  value={competence}
                  max={today()}
                  onChange={(event) => {
                    setCompetence(event.target.value)
                  }}
                />
              )}
            </Field>
          </div>
          <Field label="Buscar cliente" hint="Preenche os dados abaixo.">
            {(field) => (
              <Combobox
                {...field}
                value={partyId}
                selectedLabel={partyLabel}
                search={partySearch}
                onSearchChange={setPartySearch}
                loading={parties.isFetching}
                placeholder="Nome ou documento"
                items={(parties.data?.items ?? []).map((party) => ({
                  value: party.id,
                  label: party.legalName,
                  hint: party.document ?? undefined,
                }))}
                onValueChange={(next) => {
                  void pickParty(next)
                }}
              />
            )}
          </Field>
          <div className="flex flex-wrap gap-3">
            <Field label="Tipo" className="w-28">
              {(field) => (
                <Select
                  {...field}
                  value={documentKind}
                  onChange={(event) => {
                    setDocumentKind(event.target.value as 'cnpj' | 'cpf')
                  }}
                >
                  <option value="cnpj">CNPJ</option>
                  <option value="cpf">CPF</option>
                </Select>
              )}
            </Field>
            <Field label="Documento" className="min-w-48 flex-1">
              {(field) => (
                <Input
                  {...field}
                  inputMode="numeric"
                  value={document}
                  onChange={(event) => {
                    setDocument(event.target.value.replace(/\D/gu, ''))
                  }}
                />
              )}
            </Field>
          </div>
          <Field label="Nome ou razão social">
            {(field) => (
              <Input
                {...field}
                value={name}
                onChange={(event) => {
                  setName(event.target.value)
                }}
              />
            )}
          </Field>
          <Field label="E-mail">
            {(field) => (
              <Input
                {...field}
                type="email"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value)
                }}
              />
            )}
          </Field>
          <div className="flex flex-wrap gap-3">
            <Field
              label="CEP"
              className="w-36"
              hint={cityCode === '' ? undefined : `IBGE ${cityCode}`}
            >
              {(field) => (
                <Input
                  {...field}
                  inputMode="numeric"
                  value={postalCode}
                  onChange={(event) => {
                    const digits = event.target.value
                      .replace(/\D/gu, '')
                      .slice(0, 8)
                    setPostalCode(digits)
                    void applyCep(digits)
                  }}
                />
              )}
            </Field>
            <Field label="Rua" className="min-w-48 flex-1">
              {(field) => (
                <Input
                  {...field}
                  value={street}
                  onChange={(event) => {
                    setStreet(event.target.value)
                  }}
                />
              )}
            </Field>
            <Field label="Número" className="w-24">
              {(field) => (
                <Input
                  {...field}
                  value={number}
                  onChange={(event) => {
                    setNumber(event.target.value)
                  }}
                />
              )}
            </Field>
            <Field label="Bairro" className="min-w-40 flex-1">
              {(field) => (
                <Input
                  {...field}
                  value={district}
                  onChange={(event) => {
                    setDistrict(event.target.value)
                  }}
                />
              )}
            </Field>
          </div>
          {save.isError ? (
            <InlineAlert tone="danger">
              {message(save.error, 'Não foi possível criar a nota agora.')}
            </InlineAlert>
          ) : null}
          <DialogFooter>
            <Button variant="ghost" onClick={onClose}>
              Voltar
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={save.isPending}
              disabled={!ready}
            >
              Emitir nota
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
